import React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useFormDisable } from '../../../../../../shared/form-new/form'
import { FormNewLabel } from '../../../../../../shared/form-new/form-label'
import { StaffTag } from '../../../../../../shared/sso-workspace/workspace-shell'
import { OrgPicker } from '../../../../../../shared/org-search/org-picker'
import { StatusSwitch } from '../../../../../../shared/sso-workspace/status-switch'

// Trimmed, lower-cased email, or null -- the test allowlist's own normaliser
// (spec 4.x): looser than normalizeDomain since an allowlist entry is a full
// address, not a bare domain, so it has no "must contain a dot after an @"
// shape check beyond what the operator typed.
export const normalizeEmail = raw => {
  const v = String(raw || '').trim().toLowerCase()
  return v || null
}

// A connection is OpenID Connect (design
// edxr-workspace/.superpowers/signin-methods §1.2): "Sign in with
// Microsoft", Google, Apple and Facebook are platform sign-in methods, each
// with its own screen on the Sign-in methods page, and auth refuses any
// other driver for a connection (400 provider.driver_not_connection). So
// there is no protocol to choose: a new connection is sent as oidc.
export const DRIVER_OPTIONS = [
  { value: 'oidc', label: 'OpenID Connect' }
]

export const CONNECTION_OPTIONS = [
  { value: 'own_registration', label: 'Customer\'s own app registration' },
  { value: 'shared_app', label: 'EducationXR shared app' }
]

// The workspace header's subtitle: protocol · app registration · identifier,
// all from the row. No provider "kind" (e.g. the identity provider's
// product name): nothing on the row says it without guessing from names.
const REGISTRATION_SUBTITLE = { own_registration: 'own app registration', shared_app: 'EducationXR shared app' }
export const connectionSubtitle = provider => {
  if (!provider) return null
  const protocol = (DRIVER_OPTIONS.find(o => o.value === provider.driver) || {}).label || provider.driver
  const registration = provider.driver === 'oidc' ? REGISTRATION_SUBTITLE[provider.connection] : null
  const text = [protocol, registration].filter(Boolean).join(' · ')
  if (!text && !provider.identifier) return null
  return (
    <React.Fragment>
      {text}
      {text && provider.identifier && ' · '}
      {provider.identifier && <code>{provider.identifier}</code>}
    </React.Fragment>
  )
}

// Status is the row's `active` boolean, as the shared on/off control
// (status-switch.js: Active / Inactive with the consequence in a sentence),
// bound to the form: the choice is saved with the page's Save. servingNow:
// the saved connection serves sign-ins now, so choosing Inactive asks first.
//
// noRoute: why an ACTIVE connection still reaches nobody (audit B3: a
// connection with no bound domain that isn't marked public is offered to no
// one), shown under the control while it says Active, with a way to the
// place that fixes it (onFix).
export const FormNewActiveSwitch = ({ label, noRoute, fixText, onFix, servingNow, testers, connectionName, domains }) => {
  const { control } = useFormContext()
  const { field } = useController({ name: 'active', control })
  const formDisabled = useFormDisable()
  return (
    <StatusSwitch id="active" label={label} active={Boolean(field.value)} disabled={formDisabled}
      onChange={next => field.onChange(next)} servingNow={servingNow} testers={testers} connectionName={connectionName} domains={domains}
      undo="It takes effect when you save. To undo before then, choose Active again or discard the change."
      after={field.value && noRoute &&
        <div id="active-no-route" className="sw-hint sw-warn mt-1" role="note">
          {noRoute}
          {onFix && <> <button type="button" className="btn btn-link p-0 align-baseline" onClick={onFix}>{fixText || 'Bind a domain'}</button></>}
        </div>} />
  )
}

// A write-only credential: a client secret or a private key. The server
// never returns the stored value (agg.go ClientSecret/PrivateKeyPEM never
// reach JSON), so this always renders blank -- `hasValue` (hasClientSecret /
// hasPrivateKey) is the only signal that something is already stored, and a
// blank submit means "leave it alone" (spec 5.1/5.2).
export const FormNewSecret = ({ name, label, hasValue, textarea, subtext }) => {
  const { register } = useFormContext()
  const formDisabled = useFormDisable()

  const placeholder = hasValue ? `leave blank to keep the stored ${textarea ? 'key' : 'secret'}` : (label || name)

  return (
    <FormNewLabel name={name} label={label} subtext={subtext} secret>
      {textarea
        ? <textarea className="form-control" placeholder={placeholder} {...register(name)} disabled={formDisabled} />
        : <input className="form-control" type="password" placeholder={placeholder} {...register(name)} disabled={formDisabled} />}
    </FormNewLabel>
  )
}

// certificatePEM is the one credential-adjacent field that is NOT write-only
// -- it round-trips through JSON (agg.go), so unlike FormNewSecret it just
// shows whatever is stored.
// secret: never shown in the unsaved-changes list (the certificate).
export const FormNewTextArea = ({ name, label, subtext, placeholder, secret }) => {
  const { register } = useFormContext()
  const formDisabled = useFormDisable()
  return (
    <FormNewLabel name={name} label={label} subtext={subtext} secret={secret}>
      <textarea className="form-control" placeholder={placeholder || label || name} {...register(name)} disabled={formDisabled} />
    </FormNewLabel>
  )
}

// organizationID is a *uint on the backend (agg.go's UpdateModelFromAgg
// copies it as a plain "what the admin picked" value, not a "blank means
// leave alone" field) -- an empty string would fail to decode, so this
// stores a real number or null via useController rather than the plain
// register() a <select> normally gets, which would only ever hand back a
// string. The owner is picked from content's organization search
// (OrgPicker; owner 2026-10-13: never a list of every organization).
const PLATFORM_HELD = { value: '', label: 'Platform-held' }

export const FormNewOrganizationSelect = () => {
  const { control } = useFormContext()
  const { field } = useController({ name: 'organizationID', control })
  const formDisabled = useFormDisable()

  const handleChange = value => field.onChange(value ? Number(value) : null)

  return (
    <FormNewLabel name="organizationID" label={<React.Fragment>Owner<StaffTag /></React.Fragment>} subtext="Platform-held when no organization owns this connection.">
      <OrgPicker id="organizationID" label="Owner" disabled={formDisabled} value={field.value ? String(field.value) : ''} allOption={PLATFORM_HELD} onChange={handleChange} />
    </FormNewLabel>
  )
}

// The labels the sections show, for naming fields a blocked save needs.
const FIELD_LABELS = {
  providerName: 'name', displayName: 'sign-in button label', connection: 'app registration',
  issure: 'issuer', clientID: 'client id', clientAuthMethod: 'client auth method', expiresAt: 'expires',
  contactName: 'contact name', contactEmail: 'contact email', helpUrl: 'access-request link', helpText: 'help text',
  scopesExtra: 'additional scopes', iconSource: 'icon', iconSVGCustom: 'custom icon', signoutURL: 'sign-out endpoint', testAllowlist: 'test allowlist'
}
export const fieldLabel = key => FIELD_LABELS[key] || key

// What the save bar says when validation blocks a save: missing required
// fields first, then any field that failed another rule.
export const invalidFieldsMessage = errors => {
  const keys = Object.keys(errors || {})
  const required = keys.filter(k => errors[k] && errors[k].type === 'required').map(k => fieldLabel(k))
  const other = keys.filter(k => !(errors[k] && errors[k].type === 'required')).map(k => fieldLabel(k))
  return [
    required.length ? `Fill in the required fields: ${required.join(', ')}` : null,
    other.length ? `Check these fields: ${other.join(', ')}` : null
  ].filter(Boolean).join('. ')
}

// Provider fields the workspace edits through plain text inputs, selects
// and textareas. The API sends the nullable ones (*string in agg.go) as
// null, and write-only ones may be missing, but an input can only hold ''.
// react-hook-form compares against the loaded value, so a null field an
// admin types into and clears again would count as an unsaved change. The
// workspace hands FormNew '' for these, and puts back the loaded null (or
// absence) for any left blank, so the PUT body is what the old form sent.
export const PROVIDER_TEXT_FIELDS = [
  'identifier', 'providerName', 'displayName', 'driver', 'connection', 'issure', 'clientID', 'clientAuthMethod',
  'clientSecretNew', 'privateKeyPEMNew', 'certificatePEM', 'signoutURL', 'scopesExtra', 'tenantClaim', 'tenantValue',
  'contactName', 'contactEmail', 'helpUrl', 'helpText', 'iconSVGCustom'
]

export const toProviderFormState = provider => {
  const state = { ...provider }
  PROVIDER_TEXT_FIELDS.forEach(key => { if (state[key] === null || state[key] === undefined) state[key] = '' })
  // The Icon control's choice (auth's iconSource); a connection read from an
  // auth without it is automatic.
  if (!state.iconSource) state.iconSource = 'auto'
  return state
}

export const fromProviderFormState = (values, loaded = {}) => {
  const body = { ...values }
  PROVIDER_TEXT_FIELDS.forEach(key => {
    if (body[key] !== '') return
    if (loaded[key] === null) body[key] = null
    else if (loaded[key] === undefined) delete body[key]
  })
  // A present `domains` list replaces the provider's domain bindings on the
  // server (provider/service.go); a missing key leaves them alone. The
  // workspace doesn't edit domains (the Domains page does), so sending the
  // loaded list back would undo any domain change made since it loaded.
  delete body.domains
  delete body.pendingDomains
  // `public` and `signinMethod` mark a platform sign-in method's internal
  // row, which only auth's sign-in methods write; a connection never sends
  // them.
  delete body.public
  delete body.signinMethod
  // The icon is iconSource and iconSVGCustom (the Icon control); auth
  // resolves iconSVG and iconSVGDark on every answer, and an iconSVG it is
  // sent is an older admin's typed box. So iconSVG goes only to an auth that
  // answered no iconSource (built before the control), carrying the custom
  // SVG or what was loaded, so that auth keeps the icon it stores.
  delete body.iconSVGDark
  if (loaded.iconSource) delete body.iconSVG
  else body.iconSVG = body.iconSource === 'custom' ? (body.iconSVGCustom || null) : (loaded.iconSVG === undefined ? null : loaded.iconSVG)
  // The test list goes only when it changed: the organization that runs a
  // connection edits it too (its connection page), and auth leaves a list
  // that isn't sent alone, so a save from a page loaded earlier never puts
  // an old list back.
  if (sameList(body.testAllowlist, loaded.testAllowlist)) delete body.testAllowlist
  return body
}

const sameList = (a, b) => {
  const x = Array.isArray(a) ? a : []
  const y = Array.isArray(b) ? b : []
  return x.length === y.length && x.every((v, i) => v === y[i])
}
